/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1677715200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Mar 02 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "optimizely",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Optimizely",
          "depth": 0,
          "id": "connect-to-optimizely"
        }, {
          "value": "Syncing configuration",
          "depth": 0,
          "id": "syncing-configuration"
        }, {
          "value": "Log events",
          "depth": 1,
          "id": "log-events"
        }, {
          "value": "Visitor attributes",
          "depth": 1,
          "id": "visitor-attributes"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      h4: "h4",
      ol: "ol",
      li: "li",
      code: "code",
      h3: "h3"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "You want to provide relevant, timely interactions to your customers, and doing so heavily relies on data in your Optimizely account to be accurate and fresh. By automatically syncing customer data and events from your data warehouse into Optimizely, you no longer need to worry about data consistency because data is no longer flowing in from multiple sources in different formats, and you can focus on building world-class customer experiences."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Event Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: _jsx(_components.a, {
                  href: "https://docs.developers.optimizely.com/experimentation/v20.0.0-data/reference/post_events",
                  children: "Log Events"
                })
              })
            }), _jsx(_components.td, {
              children: "Send any event-based data associated with a user"
            }), _jsx(_components.td, {
              children: "Upsert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: _jsx(_components.a, {
                  href: "https://docs.developers.optimizely.com/experimentation/v20.0.0-data/reference/post_events",
                  children: "Visitor Attributes"
                })
              })
            }), _jsx(_components.td, {
              children: "Add customizable data or characteristics to a user"
            }), _jsx(_components.td, {
              children: "Upsert"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-optimizely",
        children: "Connect to Optimizely"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Optimizely"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to Optimizely with a personal access token and an account ID."]
      }), "\n", _jsx(_components.h4, {
        id: "authenticate-with-a-personal-access-token",
        children: "Authenticate with a personal access token"
      }), "\n", _jsx(_components.p, {
        children: "To generate a personal access token:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Navigate to your ", _jsx(_components.a, {
            href: "https://app.optimizely.com/v2/profile/api",
            children: "Optimizely profile"
          }), " and click ", _jsx(_components.strong, {
            children: "Generate New Token"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter a name for the token and click ", _jsx(_components.strong, {
            children: "Create"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You will also need the ", _jsx(_components.code, {
          children: "Project ID"
        }), ". The project ID can be found in the URL by going to your ", _jsx(_components.a, {
          href: "https://app.optimizely.com/v2/projects",
          children: "Optimizely account"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "syncing-configuration",
        children: "Syncing configuration"
      }), "\n", _jsx(_components.h3, {
        id: "log-events",
        children: "Log events"
      }), "\n", _jsx(_components.p, {
        children: "Syncing log events lets you send user actions from any source such as offline experiment data, web-page interactions, or third-party sources."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can associate events with the visitors who made them on the ", _jsx(_components.code, {
          children: "Visitor ID"
        }), " field."]
      }), "\n", _jsx(_components.h4, {
        id: "timestamps",
        children: "Timestamps"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to send a timestamp of when an event occurred. Timestamps must be sent as milliseconds since the epoch. If an event doesn't include a timestamp, the time of the API request is used."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Optimizely event fields. Optimizely events require a UUID that will be used by Optimizely to de-duplicate events that are accidentally or erroneously replayed."
      }), "\n", _jsx(_components.h3, {
        id: "visitor-attributes",
        children: "Visitor attributes"
      }), "\n", _jsx(_components.p, {
        children: "Syncing visitor attributes lets you maintain up-to-date customer data and create better audiences for personalized experiences in Optimizely. Note that you cannot create net-new properties with this API: attributes must be created within Optimizely before using this endpoint. Since Optimizely audience membership is handled by whether a visitor's attributes satisfy the conditions required for the audience, sending data to this endpoint controls audience membership in Optimizely."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can associate attributes to visitors on the ", _jsx(_components.code, {
          children: "Visitor ID"
        }), " field."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Optimizely visitor attributes. Attributes must be created within Optimizely before creating mappings."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
